import type { GenericSchema, InferOutput } from "valibot";
import {
  array,
  boolean,
  literal,
  object,
  objectAsync,
  optional,
  partialCheck,
  pipe,
  rawCheck,
  string,
  variant,
} from "valibot";
import { initIntegrationDefinition } from "../Integration";
import type { TranslationKey } from "../../state/i18n";
import { useTranslation } from "../../state/i18n";
import type { IncompleteIntegrationDefinition } from "../types";
import { isEmpty, isNil, isString } from "lodash-es";
import { MAX_CHARACTERS_2XL, MAX_CHARACTERS_MD, MAX_CHARACTERS_SM, MAX_TOTAL_RECIPIENTS } from "../../constants";
import { Checkbox } from "../../components/checkbox/Checkbox";
import Wysiwyg from "../../components/wysiwyg/Wysiwyg";
import { FieldsAccordion } from "../../components/accordion/Accordion";
import CheckboxFieldListItem from "../../components/listItem/CheckboxFieldListItem";
import { useFormServices } from "../../services/formServices";
import type { PinFieldProperties } from "../../fields/media/PinField";
import { Toggle } from "../../components/toggle/Toggle";
import { Show } from "solid-js";
import MixedInput from "../../components/inputs/MixedInput";
import { metaVariableOption, toFieldValueOptions } from "../../utils/selectOptionsUtil";
import { HtmlInputWithPreview } from "../../components/inputs/HtmlInput";
import { parseToRecipients } from "../integrationUtils";
import {
  parseStringTemplate,
  splitLiteralsOnComma,
  stringTemplateToString,
  stringTemplateToStringArray,
} from "../../utils/stringTemplateUtil";
import { errorTranslation, invalidPlaceholderError, resource } from "../../utils/validation/base";
import { findInvalidPlaceholders } from "../../utils/freeMarkerPlaceholderUtil";
import CustomerResourceInput from "../../fields/CustomerResourceInput";
import ApiService from "../../services/ApiService";
import { WarningModal } from "../../components/modal/Modal";
import type { FieldType, Nullish } from "../../types";
import { Scope } from "../../types";
import FieldService from "../../services/FieldService";
import { fieldReferenceOfTypes } from "../../utils/validation/references";
import { recipient } from "../../utils/validation/recipient";
import FeatureService from "../../services/FeatureService";
import { resourceAsync } from "../../utils/validation/async";
import { stringTemplate } from "../../utils/validation/stringTemplate";
import { insertBetween } from "../../utils/arrayUtil";
import { RECIPIENT_ALLOWED_PLACEHOLDER_FIELDS } from "../../utils/fieldUtil";
import { RECIPIENT_ALLOWED_PLACEHOLDER_VARIABLES, toVariableValueOption } from "../../variables/variableUtil";
import type { VariableType } from "../../variables/types";
import { createDynamicValueOptions } from "../../utils/menu/searchableMenuUtil";
import SeparatorLine from "../../components/SeparatorLine";
import { useModalManager } from "../../components/modal/ModalManager";
import MetaVariableReference from "../../metaVariables/MetaVariableReference";
import type { MetaVariableId } from "../../metaVariables/metaVariable";
import { integrationsIcons } from "../IntegrationIcons";
import { keyFromSelector } from "i18next";
import { FieldConfig } from "../../fields/types.ts";

const SUBJECT_ALLOWED_PLACEHOLDER_FIELDS: FieldType[] = [
  "address",
  "barcode",
  "calculation",
  "checkbox",
  "currentLocation",
  "date",
  "dateTime",
  "email",
  "iban",
  "licensePlate",
  "location",
  "lookup",
  "number",
  "phone",
  "price",
  "radio",
  "rating",
  "readOnlyText",
  "search",
  "slider",
  "smiley",
  "stopwatch",
  "text",
  "textArea",
  "time",
  "timeDifference",
];
const PDF_FILENAME_ALLOWED_PLACEHOLDER_FIELDS: FieldType[] = [
  "address",
  "barcode",
  "calculation",
  "checkbox",
  "currentLocation",
  "date",
  "dateTime",
  "email",
  "iban",
  "licensePlate",
  "location",
  "lookup",
  "number",
  "phone",
  "price",
  "radio",
  "rating",
  "readOnlyText",
  "search",
  "slider",
  "smiley",
  "stopwatch",
  "text",
  "textArea",
  "time",
  "timeDifference",
];
const EMAIL_CONTENT_ALLOWED_PLACEHOLDER_FIELDS: FieldType[] = [
  "address",
  "barcode",
  "calculation",
  "catalogue",
  "checkbox",
  "currentLocation",
  "date",
  "dateTime",
  "email",
  "file",
  "iban",
  "licensePlate",
  "location",
  "lookup",
  "number",
  "phone",
  "photo",
  "price",
  "radio",
  "rating",
  "readOnlyText",
  "search",
  "signature",
  "slider",
  "smiley",
  "stopwatch",
  "subform",
  "text",
  "textArea",
  "time",
  "timeDifference",
  "video",
];

const SUBJECT_ALLOWED_PLACEHOLDER_VARIABLES: VariableType[] = ["string", "boolean", "number", "date", "datetime"];
const PDF_FILENAME_ALLOWED_PLACEHOLDER_VARIABLES: VariableType[] = ["string", "boolean", "number", "date", "datetime"];
const EMAIL_CONTENT_ALLOWED_PLACEHOLDER_VARIABLES: VariableType[] = ["string", "boolean", "number", "date", "datetime"];

const SUBJECT_ALLOWED_PLACEHOLDER_META_VARIABLES: MetaVariableId[] = [
  "info.userId",
  "meta.location.longitude",
  "meta.location.latitude",
  "meta.registrationDate",
  "meta.guid",
  "meta.taskId",
  "meta.serialNumber",
  "info.formName",
  "info.summary",
];
const PDF_FILENAME_ALLOWED_PLACEHOLDER_META_VARIABLES: MetaVariableId[] = [
  "info.userId",
  "meta.location.longitude",
  "meta.location.latitude",
  "meta.registrationDate",
  "meta.guid",
  "meta.taskId",
  "meta.serialNumber",
  "info.formName",
  "info.summary",
];
const EMAIL_CONTENT_ALLOWED_PLACEHOLDER_META_VARIABLES: MetaVariableId[] = [
  "info.userId",
  "meta.location.longitude",
  "meta.location.latitude",
  "meta.registrationDate",
  "meta.guid",
  "meta.taskId",
  "meta.serialNumber",
  "info.formName",
  "info.summary",
];

export type EmailConfigurationDto = {
  staticRecipients: string[];
  dynamicRecipients: string[];
  carbonCopyRecipients?: string[];
  blindCarbonCopyRecipients?: string[];
  subject: string;
  content: string;
  pdfContent: Nullish<string>;
  pdfFilename: Nullish<string>;
  fromName: Nullish<string>;
  replyTo: Nullish<string>;
  pdfHeaderLogoResourceId?: string;
  configuration: {
    attachments: string[];
    attachImages: boolean;
    embedImages: boolean;
    attachPdf: boolean;
    simpleMode: boolean;
    useHeaderAndFooter: boolean;
    hideNoValues: boolean;
    horizontalOrientation: boolean;
    distributedGeneration: boolean;
    legacyGeneration: boolean;
  };
};

const ResourceSchema = objectAsync({
  pdfHeaderLogoResourceId: resourceAsync(),
});

const getDataNames = (fields: FieldConfig[]) => fields.map(({ properties }) => properties.data_name).filter(isString);

// Like the server, only the subforms of the form itself are checked, not nested ones
const getRootSubformDataNames = () =>
  getDataNames(FieldService.getFieldsByTypes(["subform"]).filter(({ scope }) => scope === Scope.ROOT));

const customPdfContent = () =>
  pipe(
    string(),
    rawCheck(({ dataset, addIssue }) => {
      if (!dataset.typed) {
        return;
      }

      // Only the first one is mentioned, the editor underlines all of them
      const [first] = findInvalidPlaceholders(dataset.value, getRootSubformDataNames());
      if (!isNil(first)) {
        addIssue({ message: invalidPlaceholderError(first) });
      }
    }),
  );

const BaseSchema = {
  fromName: stringTemplate(
    SUBJECT_ALLOWED_PLACEHOLDER_FIELDS,
    SUBJECT_ALLOWED_PLACEHOLDER_VARIABLES,
    SUBJECT_ALLOWED_PLACEHOLDER_META_VARIABLES,
    MAX_CHARACTERS_SM,
    false,
  ),
  replyTo: stringTemplate(
    SUBJECT_ALLOWED_PLACEHOLDER_FIELDS,
    SUBJECT_ALLOWED_PLACEHOLDER_VARIABLES,
    ["info.userId"],
    MAX_CHARACTERS_SM,
    false,
  ),
  recipients: array(recipient()),
  cc: array(recipient()),
  bcc: array(recipient()),
  subject: stringTemplate(
    SUBJECT_ALLOWED_PLACEHOLDER_FIELDS,
    SUBJECT_ALLOWED_PLACEHOLDER_VARIABLES,
    SUBJECT_ALLOWED_PLACEHOLDER_META_VARIABLES,
    MAX_CHARACTERS_MD,
  ),
  content: stringTemplate(
    EMAIL_CONTENT_ALLOWED_PLACEHOLDER_FIELDS,
    EMAIL_CONTENT_ALLOWED_PLACEHOLDER_VARIABLES,
    EMAIL_CONTENT_ALLOWED_PLACEHOLDER_META_VARIABLES,
    MAX_CHARACTERS_2XL,
    true,
    "${",
    {
      location: { includeMapAndAddress: true },
    },
  ),
  attachFiles: boolean(),
  pinFieldsToAttach: array(fieldReferenceOfTypes(["pin"])),
  attachPdf: boolean(),
  pdfFilename: stringTemplate(
    PDF_FILENAME_ALLOWED_PLACEHOLDER_FIELDS,
    PDF_FILENAME_ALLOWED_PLACEHOLDER_VARIABLES,
    PDF_FILENAME_ALLOWED_PLACEHOLDER_META_VARIABLES,
    MAX_CHARACTERS_SM,
  ),
  hideNoValues: boolean(),
  useHeaderAndFooter: boolean(),
  pdfHeaderLogoResourceId: resource(),
};

const Schema = pipe(
  // The custom PDF content is not used (nor shown) in simple mode, so it should not block saving then
  variant("simpleMode", [
    object({ ...BaseSchema, simpleMode: literal(true), pdfContent: optional(string()) }),
    object({ ...BaseSchema, simpleMode: literal(false), pdfContent: optional(customPdfContent()) }),
  ]),
  partialCheck(
    [["recipients"], ["cc"], ["bcc"]],
    ({ recipients, cc, bcc }) => recipients.length + cc.length + bcc.length <= MAX_TOTAL_RECIPIENTS,
    errorTranslation(
      keyFromSelector(($) => $.VALIDATION_MAX_TOTAL_RECIPIENTS),
      { max: MAX_TOTAL_RECIPIENTS },
    ),
  ),
) satisfies GenericSchema<InferOutput<typeof ResourceSchema>>;

export type EmailIntegrationConfiguration = InferOutput<typeof Schema>;
const EmailIntegration: IncompleteIntegrationDefinition<EmailIntegrationConfiguration, EmailConfigurationDto> = {
  type: "mail",
  legacy: {
    namespace: "com.moreapp",
    key: "mail",
    version: 1,
  },
  schema: Schema,
  resourceSchema: ResourceSchema,
  icon: integrationsIcons["mail"],
  titleKey: keyFromSelector(($) => $.EMAIL_INTEGRATION_TITLE),
  descriptionKey: "" as TranslationKey,
  defaultProperties: (t, fields?: FieldConfig[]) => {
    const pinFields = fields?.filter((field) => field.type === "pin") ?? [];

    return {
      fromName: [],
      replyTo: [],
      recipients: [{ type: "metaVariable", value: MetaVariableReference.from("sys.user") }],
      cc: [],
      bcc: [],
      subject: parseStringTemplate(
        t(($) => $.EMAIL_INTEGRATION_CONFIGURATION_SUBJECT_DEFAULT_VALUE),
        [],
        [],
      ),
      content: parseStringTemplate(
        t(($) => $.EMAIL_INTEGRATION_CONFIGURATION_CONTENT_DEFAULT_VALUE),
        [],
        [],
      ),
      attachFiles: true,
      pinFieldsToAttach: pinFields.map((field) => field.uid),
      attachPdf: true,
      pdfFilename: parseStringTemplate(
        t(($) => $.EMAIL_INTEGRATION_CONFIGURATION_PDF_FILENAME_DEFAULT_VALUE),
        [],
        [],
      ),
      hideNoValues: true,
      useHeaderAndFooter: true,
      simpleMode: true,
    };
  },
  propertyKeys: {
    recipients: keyFromSelector(($) => $.EMAIL_INTEGRATION_CONFIGURATION_RECIPIENTS_LABEL),
    cc: keyFromSelector(($) => $.EMAIL_INTEGRATION_CONFIGURATION_CC_LABEL),
    bcc: keyFromSelector(($) => $.EMAIL_INTEGRATION_CONFIGURATION_BCC_LABEL),
    subject: keyFromSelector(($) => $.EMAIL_INTEGRATION_CONFIGURATION_SUBJECT_LABEL),
    content: keyFromSelector(($) => $.EMAIL_INTEGRATION_CONFIGURATION_CONTENT_LABEL),
    pdfContent: keyFromSelector(($) => $.EMAIL_INTEGRATION_CONFIGURATION_PDF_CONTENT_LABEL),
    pdfFilename: keyFromSelector(($) => $.EMAIL_INTEGRATION_CONFIGURATION_PDF_FILENAME_LABEL),
    fromName: keyFromSelector(($) => $.EMAIL_INTEGRATION_CONFIGURATION_FROM_NAME_LABEL),
    replyTo: keyFromSelector(($) => $.EMAIL_INTEGRATION_CONFIGURATION_REPLY_TO_LABEL),
    pdfHeaderLogoResourceId: keyFromSelector(
      ($) => $.EMAIL_INTEGRATION_CONFIGURATION_PDF_HEADER_LOGO_RESOURCE_ID_LABEL,
    ),
    pinFieldsToAttach: keyFromSelector(($) => $.EMAIL_INTEGRATION_CONFIGURATION_PIN_FIELDS_TO_ATTACH_LABEL),
    attachFiles: keyFromSelector(($) => $.EMAIL_INTEGRATION_CONFIGURATION_ATTACH_FILES_LABEL),
    attachPdf: keyFromSelector(($) => $.EMAIL_INTEGRATION_CONFIGURATION_ATTACH_PDF_LABEL),
    simpleMode: keyFromSelector(($) => $.EMAIL_INTEGRATION_CONFIGURATION_SIMPLE_MODE_LABEL),
    useHeaderAndFooter: keyFromSelector(($) => $.EMAIL_INTEGRATION_CONFIGURATION_USE_HEADER_AND_FOOTER_LABEL),
    hideNoValues: keyFromSelector(($) => $.EMAIL_INTEGRATION_CONFIGURATION_HIDE_NO_VALUES_LABEL),
  },
  fromDto: (configuration, fields, variables) => {
    // Static recipients
    const staticRecipients = (configuration.staticRecipients ?? []).map((value) => ({
      type: "literal" as const,
      value,
    }));
    // Not all items in dynamic recipients are wrapped with '${...}', which is needed if we want to use 'parseStringTemplate' for parsing
    const dynamicRecipientsAsString = configuration.dynamicRecipients
      .map((item) => (!item.startsWith("${") ? `$\{${item}}` : item))
      .join("");
    const dynamicRecipients = parseStringTemplate(dynamicRecipientsAsString, fields, variables);

    // Placeholders in 'CC' and 'BCC' are stored as plain datanames (without delimeters), in order to be able to parse
    // them later on we convert them to id's
    const convertDataNamesToIds = (items: string[]): string[] =>
      items.map((item) => {
        const field = fields.find((field) => field.properties.data_name === item);
        if (!isNil(field)) {
          return field.uid.toString();
        }

        const variable = variables.find((variable) => variable.dataName === item);
        if (!isNil(variable)) {
          return variable.uid.toString();
        }

        return item;
      });

    return {
      recipients: [...staticRecipients, ...dynamicRecipients],
      cc: isNil(configuration.carbonCopyRecipients)
        ? []
        : parseToRecipients(convertDataNamesToIds(configuration.carbonCopyRecipients), variables),
      bcc: isNil(configuration.blindCarbonCopyRecipients)
        ? []
        : parseToRecipients(convertDataNamesToIds(configuration.blindCarbonCopyRecipients), variables),
      subject: parseStringTemplate(configuration.subject, fields, variables),
      content: parseStringTemplate(configuration.content, fields, variables),
      pdfContent: configuration.pdfContent ?? undefined,
      pdfFilename: parseStringTemplate(configuration.pdfFilename, fields, variables),
      fromName: parseStringTemplate(configuration.fromName, fields, variables),
      replyTo: parseStringTemplate(configuration.replyTo, fields, variables),
      pdfHeaderLogoResourceId: configuration.pdfHeaderLogoResourceId,
      pinFieldsToAttach: (configuration.configuration.attachments ?? [])
        .map((dataName) => fields.find((field) => field.type === "pin" && field.properties.data_name === dataName)?.uid)
        .filter((item) => !isNil(item)),
      simpleMode: configuration.configuration.simpleMode,
      attachFiles: configuration.configuration.attachImages,
      attachPdf: configuration.configuration.attachPdf,
      hideNoValues: configuration.configuration.hideNoValues,
      useHeaderAndFooter: configuration.configuration.useHeaderAndFooter,
    };
  },
  toDto: (configuration, fields, variables) => {
    const staticRecipients = configuration.recipients.filter((r) => r.type === "literal").map((r) => r.value);
    const dynamicRecipients = configuration.recipients.filter(
      (r) => r.type === "field" || r.type === "variable" || r.type === "metaVariable",
    );

    return {
      staticRecipients,
      dynamicRecipients: stringTemplateToStringArray(dynamicRecipients, fields, variables),
      carbonCopyRecipients: stringTemplateToStringArray(configuration.cc, fields, variables),
      blindCarbonCopyRecipients: stringTemplateToStringArray(configuration.bcc, fields, variables),
      subject: stringTemplateToString(configuration.subject, fields, variables),
      content: stringTemplateToString(configuration.content, fields, variables, "${", "", true),
      pdfContent: configuration.pdfContent,
      pdfFilename: stringTemplateToString(configuration.pdfFilename, fields, variables),
      fromName: stringTemplateToString(configuration.fromName, fields, variables),
      replyTo: stringTemplateToString(configuration.replyTo, fields, variables),
      pdfHeaderLogoResourceId: configuration.pdfHeaderLogoResourceId ?? undefined,
      configuration: {
        embedImages: true,
        horizontalOrientation: false,
        distributedGeneration: false,
        legacyGeneration: false,
        attachImages: configuration.attachFiles,
        attachments: configuration.pinFieldsToAttach
          .map((fieldId) => fields.find((field) => fieldId.equals(field.uid))?.properties.data_name as string)
          .filter((item) => !isNil(item)),
        attachPdf: configuration.attachPdf,
        simpleMode: configuration.simpleMode,
        useHeaderAndFooter: configuration.useHeaderAndFooter,
        hideNoValues: configuration.hideNoValues,
      },
    };
  },
  EditItem: (props) => {
    const { t } = useTranslation();
    const { open } = useModalManager();
    const { fieldService, variableService } = useFormServices();

    const resetPdfLayout = async () => {
      const defaultContent = await ApiService.generateDefaultPdfContent(
        fieldService.getFields(),
        variableService.getVariables(),
        {
          hideNoValues: props.integration.configuration.hideNoValues,
          horizontalOrientation: false,
        },
      );
      props.updateConfiguration("pdfContent", defaultContent);
    };

    const toggleCustomPdf = async (enabled: boolean) => {
      if (enabled && isEmpty(props.integration.configuration.pdfContent)) {
        await resetPdfLayout();
      }
      props.updateConfiguration("simpleMode", !enabled);
    };

    return (
      <>
        <MixedInput
          label={t(props.propertyKeys.fromName)}
          value={props.integration.configuration.fromName}
          parse={(value) =>
            parseStringTemplate(
              value,
              fieldService.getFieldsByTypes(SUBJECT_ALLOWED_PLACEHOLDER_FIELDS),
              variableService.getVariablesByTypes(SUBJECT_ALLOWED_PLACEHOLDER_VARIABLES),
            )
          }
          options={createDynamicValueOptions(t, {
            fields: fieldService
              .getFieldsByTypes(SUBJECT_ALLOWED_PLACEHOLDER_FIELDS)
              .flatMap((field) => toFieldValueOptions(field)),
            subforms: [],
            customVariables: variableService
              .getVariablesByTypes(SUBJECT_ALLOWED_PLACEHOLDER_VARIABLES)
              .map(toVariableValueOption),
            metaVariables: SUBJECT_ALLOWED_PLACEHOLDER_META_VARIABLES.map(metaVariableOption),
          })}
          onChange={(value) => props.updateConfiguration("fromName", value)}
          errorMessage={props.getError("fromName")}
          info={{
            title: t(props.propertyKeys.fromName),
            alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.recipients }),
            description: t(($) => $.EMAIL_INTEGRATION_CONFIGURATION_FROM_NAME_DESCRIPTION),
          }}
        />

        <MixedInput
          label={t(props.propertyKeys.replyTo)}
          value={props.integration.configuration.replyTo}
          parse={(value) =>
            parseStringTemplate(
              value,
              fieldService.getFieldsByTypes(RECIPIENT_ALLOWED_PLACEHOLDER_FIELDS),
              variableService.getVariablesByTypes(RECIPIENT_ALLOWED_PLACEHOLDER_VARIABLES),
            )
          }
          options={createDynamicValueOptions(t, {
            fields: fieldService
              .getFieldsByTypes(RECIPIENT_ALLOWED_PLACEHOLDER_FIELDS)
              .flatMap((field) => toFieldValueOptions(field)),
            subforms: [],
            customVariables: variableService
              .getVariablesByTypes(RECIPIENT_ALLOWED_PLACEHOLDER_VARIABLES)
              .map(toVariableValueOption),
            metaVariables: [metaVariableOption("info.userId")],
          })}
          onChange={(value) => {
            props.updateConfiguration("replyTo", value);
          }}
          errorMessage={props.getError("replyTo")}
          info={{
            title: t(props.propertyKeys.replyTo),
            alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.replyTo }),
            description: t(($) => $.EMAIL_INTEGRATION_CONFIGURATION_REPLY_TO_DESCRIPTION),
          }}
        />

        <MixedInput
          label={t(props.propertyKeys.recipients)}
          value={insertBetween(props.integration.configuration.recipients, { type: "literal", value: ", " })}
          parse={(value) =>
            parseStringTemplate(
              value,
              fieldService.getFieldsByTypes(RECIPIENT_ALLOWED_PLACEHOLDER_FIELDS),
              variableService.getVariablesByTypes(RECIPIENT_ALLOWED_PLACEHOLDER_VARIABLES),
            )
          }
          options={createDynamicValueOptions(t, {
            fields: fieldService
              .getFieldsByTypes(RECIPIENT_ALLOWED_PLACEHOLDER_FIELDS)
              .flatMap((field) => toFieldValueOptions(field)),
            subforms: [],
            customVariables: variableService
              .getVariablesByTypes(RECIPIENT_ALLOWED_PLACEHOLDER_VARIABLES)
              .map(toVariableValueOption),
            metaVariables: [metaVariableOption("sys.user")],
          })}
          onChange={(value) => {
            props.updateConfiguration("recipients", splitLiteralsOnComma(value));
          }}
          errorMessage={props.getError("recipients")}
          info={{
            title: t(props.propertyKeys.recipients),
            alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.recipients }),
            description: t(($) => $.EMAIL_INTEGRATION_CONFIGURATION_RECIPIENTS_DESCRIPTION),
          }}
        />

        <MixedInput
          label={t(props.propertyKeys.cc)}
          value={insertBetween(props.integration.configuration.cc, { type: "literal", value: ", " })}
          parse={(value) =>
            parseStringTemplate(
              value,
              fieldService.getFieldsByTypes(RECIPIENT_ALLOWED_PLACEHOLDER_FIELDS),
              variableService.getVariablesByTypes(RECIPIENT_ALLOWED_PLACEHOLDER_VARIABLES),
            )
          }
          options={createDynamicValueOptions(t, {
            fields: fieldService
              .getFieldsByTypes(RECIPIENT_ALLOWED_PLACEHOLDER_FIELDS)
              .flatMap((field) => toFieldValueOptions(field)),
            subforms: [],
            customVariables: variableService
              .getVariablesByTypes(RECIPIENT_ALLOWED_PLACEHOLDER_VARIABLES)
              .map(toVariableValueOption),
            metaVariables: [metaVariableOption("sys.user")],
          })}
          onChange={(value) => {
            props.updateConfiguration("cc", splitLiteralsOnComma(value));
          }}
          errorMessage={props.getError("cc")}
          info={{
            title: t(props.propertyKeys.cc),
            alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.cc }),
            description: t(($) => $.EMAIL_INTEGRATION_CONFIGURATION_CC_DESCRIPTION),
          }}
        />

        <MixedInput
          label={t(props.propertyKeys.bcc)}
          value={insertBetween(props.integration.configuration.bcc, { type: "literal", value: ", " })}
          parse={(value) =>
            parseStringTemplate(
              value,
              fieldService.getFieldsByTypes(RECIPIENT_ALLOWED_PLACEHOLDER_FIELDS),
              variableService.getVariablesByTypes(RECIPIENT_ALLOWED_PLACEHOLDER_VARIABLES),
            )
          }
          options={createDynamicValueOptions(t, {
            fields: fieldService
              .getFieldsByTypes(RECIPIENT_ALLOWED_PLACEHOLDER_FIELDS)
              .flatMap((field) => toFieldValueOptions(field)),
            subforms: [],
            customVariables: variableService
              .getVariablesByTypes(RECIPIENT_ALLOWED_PLACEHOLDER_VARIABLES)
              .map(toVariableValueOption),
            metaVariables: [metaVariableOption("sys.user")],
          })}
          onChange={(value) => {
            props.updateConfiguration("bcc", splitLiteralsOnComma(value));
          }}
          errorMessage={props.getError("bcc")}
          info={{
            title: t(props.propertyKeys.bcc),
            alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.bcc }),
            description: t(($) => $.EMAIL_INTEGRATION_CONFIGURATION_BCC_DESCRIPTION),
          }}
        />

        <MixedInput
          label={t(props.propertyKeys.subject)}
          value={props.integration.configuration.subject}
          parse={(value) =>
            parseStringTemplate(
              value,
              fieldService.getFieldsByTypes(SUBJECT_ALLOWED_PLACEHOLDER_FIELDS),
              variableService.getVariablesByTypes(SUBJECT_ALLOWED_PLACEHOLDER_VARIABLES),
            )
          }
          options={createDynamicValueOptions(t, {
            fields: fieldService
              .getFieldsByTypes(SUBJECT_ALLOWED_PLACEHOLDER_FIELDS)
              .flatMap((field) => toFieldValueOptions(field)),
            subforms: [],
            customVariables: variableService
              .getVariablesByTypes(SUBJECT_ALLOWED_PLACEHOLDER_VARIABLES)
              .map(toVariableValueOption),
            metaVariables: SUBJECT_ALLOWED_PLACEHOLDER_META_VARIABLES.map(metaVariableOption),
          })}
          onChange={(value) => {
            props.updateConfiguration("subject", value);
          }}
          errorMessage={props.getError("subject")}
          required
          info={{
            title: t(props.propertyKeys.subject),
            alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.subject }),
            description: t(($) => $.EMAIL_INTEGRATION_CONFIGURATION_SUBJECT_DESCRIPTION),
          }}
        />

        <Wysiwyg
          ariaLabel={t(props.propertyKeys.content)}
          placeholderMenuOptions={createDynamicValueOptions(t, {
            fields: fieldService
              .getFieldsByTypes(EMAIL_CONTENT_ALLOWED_PLACEHOLDER_FIELDS)
              .flatMap((field) => toFieldValueOptions(field, { location: { includeMapAndAddress: true } })),
            subforms: [],
            customVariables: variableService
              .getVariablesByTypes(EMAIL_CONTENT_ALLOWED_PLACEHOLDER_VARIABLES)
              .map(toVariableValueOption),
            metaVariables: EMAIL_CONTENT_ALLOWED_PLACEHOLDER_META_VARIABLES.map(metaVariableOption),
          })}
          content={props.integration.configuration.content ?? ""}
          onChange={(value) => props.updateConfiguration("content", value)}
          errorMessage={props.getError("content")}
          parse={(value) =>
            parseStringTemplate(
              value,
              fieldService.getFieldsByTypes(EMAIL_CONTENT_ALLOWED_PLACEHOLDER_FIELDS),
              variableService.getVariablesByTypes(EMAIL_CONTENT_ALLOWED_PLACEHOLDER_VARIABLES),
            )
          }
        />

        <Checkbox
          label={t(props.propertyKeys.attachFiles)}
          block
          checked={props.integration.configuration.attachFiles}
          setChecked={(value) => {
            props.updateConfiguration("attachFiles", value);
          }}
          info={{
            title: t(props.propertyKeys.attachFiles),
            alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.attachFiles }),
            description: t(($) => $.EMAIL_INTEGRATION_CONFIGURATION_ATTACH_FILES_DESCRIPTION),
          }}
        />

        <FieldsAccordion
          items={[
            {
              label: t(props.propertyKeys.pinFieldsToAttach),
              key: "pinFields",
              components: fieldService.getFieldsByType<PinFieldProperties>("pin").map((field) => {
                const pinFieldsToAttach = props.integration.configuration.pinFieldsToAttach;

                return {
                  Component: () => (
                    <CheckboxFieldListItem
                      checked={pinFieldsToAttach.includes(field.uid)}
                      setChecked={(value) => {
                        const newValue = value
                          ? [...pinFieldsToAttach, field.uid]
                          : pinFieldsToAttach.filter((fieldId) => !fieldId.equals(field.uid));
                        props.updateConfiguration("pinFieldsToAttach", newValue);
                      }}
                      title={`${field.properties.data_name}.pdf`}
                      category="media"
                      icon="pin"
                      dataName={field.properties.data_name}
                    />
                  ),
                  filterKey: "",
                };
              }),
            },
          ]}
        />

        <SeparatorLine />

        <h2 class="text-header-xl text-primary">{t(($) => $.EMAIL_INTEGRATION_SECTION_PDF_TITLE)}</h2>

        <div class="flex flex-col gap-4">
          <Toggle
            label={t(props.propertyKeys.attachPdf)}
            block
            checked={props.integration.configuration.attachPdf}
            setChecked={(value) => {
              props.updateConfiguration("attachPdf", value);
            }}
            info={{
              title: t(props.propertyKeys.attachPdf),
              alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.attachPdf }),
              description: t(($) => $.EMAIL_INTEGRATION_CONFIGURATION_ATTACH_PDF_DESCRIPTION),
            }}
          />

          <Show when={props.integration.configuration.attachPdf}>
            <div class="flex flex-col gap-6">
              <MixedInput
                label={t(props.propertyKeys.pdfFilename)}
                value={props.integration.configuration.pdfFilename}
                parse={(value) =>
                  parseStringTemplate(
                    value,
                    fieldService.getFieldsByTypes(PDF_FILENAME_ALLOWED_PLACEHOLDER_FIELDS),
                    variableService.getVariablesByTypes(PDF_FILENAME_ALLOWED_PLACEHOLDER_VARIABLES),
                  )
                }
                options={createDynamicValueOptions(t, {
                  fields: fieldService
                    .getFieldsByTypes(PDF_FILENAME_ALLOWED_PLACEHOLDER_FIELDS)
                    .flatMap((field) => toFieldValueOptions(field)),
                  subforms: [],
                  customVariables: variableService
                    .getVariablesByTypes(PDF_FILENAME_ALLOWED_PLACEHOLDER_VARIABLES)
                    .map(toVariableValueOption),
                  metaVariables: PDF_FILENAME_ALLOWED_PLACEHOLDER_META_VARIABLES.map(metaVariableOption),
                })}
                required
                onChange={(value) => {
                  props.updateConfiguration("pdfFilename", value);
                }}
                errorMessage={props.getError("pdfFilename")}
                info={{
                  title: t(props.propertyKeys.pdfFilename),
                  alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, {
                    property: props.propertyKeys.pdfFilename,
                  }),
                  description: t(($) => $.EMAIL_INTEGRATION_CONFIGURATION_PDF_FILENAME_DESCRIPTION),
                }}
              />

              <Checkbox
                label={t(props.propertyKeys.hideNoValues)}
                block
                checked={props.integration.configuration.hideNoValues}
                setChecked={(value) => {
                  props.updateConfiguration("hideNoValues", value);
                }}
                info={{
                  title: t(props.propertyKeys.hideNoValues),
                  alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, {
                    property: props.propertyKeys.hideNoValues,
                  }),
                  description: t(($) => $.EMAIL_INTEGRATION_CONFIGURATION_HIDE_NO_VALUES_DESCRIPTION),
                }}
              />

              <div class="flex flex-col gap-4">
                <Toggle
                  label={t(props.propertyKeys.useHeaderAndFooter)}
                  block
                  checked={props.integration.configuration.useHeaderAndFooter}
                  setChecked={(value) => {
                    props.updateConfiguration("useHeaderAndFooter", value);
                  }}
                  info={{
                    title: t(props.propertyKeys.useHeaderAndFooter),
                    alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, {
                      property: props.propertyKeys.useHeaderAndFooter,
                    }),
                    description: t(($) => $.EMAIL_INTEGRATION_CONFIGURATION_USE_HEADER_AND_FOOTER_DESCRIPTION),
                  }}
                />

                <Show when={props.integration.configuration.useHeaderAndFooter}>
                  <CustomerResourceInput
                    label={t(props.propertyKeys.pdfHeaderLogoResourceId)}
                    mediaModal={{ defaultTab: "library" }}
                    onClear={() => {
                      props.updateConfiguration("pdfHeaderLogoResourceId", null);
                    }}
                    value={props.integration.configuration.pdfHeaderLogoResourceId ?? null}
                    onSelect={(file) => {
                      props.updateConfiguration("pdfHeaderLogoResourceId", file.id);
                    }}
                    errorMessage={props.getError("pdfHeaderLogoResourceId")}
                    buttonLabel={t(($) => $.EMAIL_INTEGRATION_CONFIGURATION_PDF_LOGO_BUTTON_LABEL)}
                    info={{
                      title: t(props.propertyKeys.pdfHeaderLogoResourceId),
                      alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, {
                        property: props.propertyKeys.pdfHeaderLogoResourceId,
                      }),
                      description: t(($) => $.EMAIL_INTEGRATION_CONFIGURATION_PDF_HEADER_LOGO_RESOURCE_ID_DESCRIPTION),
                    }}
                  />
                </Show>
              </div>

              <Show when={FeatureService.hasFeature("HOOKS")}>
                <div class="flex flex-col gap-4">
                  <Toggle
                    label={t(props.propertyKeys.simpleMode)}
                    block
                    checked={!props.integration.configuration.simpleMode}
                    setChecked={toggleCustomPdf}
                    info={{
                      title: t(props.propertyKeys.simpleMode),
                      alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, {
                        property: props.propertyKeys.simpleMode,
                      }),
                      description: t(($) => $.EMAIL_INTEGRATION_CONFIGURATION_SIMPLE_MODE_DESCRIPTION),
                      href: t(($) => $.EMAIL_INTEGRATION_CONFIGURATION_SIMPLE_MODE_INFO_LINK),
                    }}
                  />

                  <Show when={!props.integration.configuration.simpleMode}>
                    <HtmlInputWithPreview
                      label=""
                      ariaLabel={t(props.propertyKeys.pdfContent)}
                      message={{
                        label: t(($) => $.EMAIL_INTEGRATION_CONFIGURATION_PDF_CONTENT_EDITOR_INFORMATIVE_MESSAGE),
                        action: {
                          label: t(($) => $.EMAIL_INTEGRATION_CONFIGURATION_PDF_CONTENT_RESET_LAYOUT_ACTION_LABEL),
                          onClick: () =>
                            open({
                              content: () => (
                                <WarningModal
                                  confirmLabel={t(($) => $.CONTINUE_BUTTON)}
                                  cancelLabel={t(($) => $.CANCEL_BUTTON)}
                                  title={t(
                                    ($) => $.EMAIL_INTEGRATION_CONFIGURATION_PDF_CONTENT_RESET_LAYOUT_MODAL_TITLE,
                                  )}
                                  description={t(
                                    ($) => $.EMAIL_INTEGRATION_CONFIGURATION_PDF_CONTENT_RESET_LAYOUT_MODAL_DESCRIPTION,
                                  )}
                                  onConfirm={() => resetPdfLayout()}
                                />
                              ),
                            }),
                        },
                      }}
                      value={props.integration.configuration.pdfContent ?? ""}
                      onChange={(value) => props.updateConfiguration("pdfContent", value)}
                      errorMessage={props.getError("pdfContent")}
                      findInvalidRanges={(content) =>
                        findInvalidPlaceholders(content, getDataNames(fieldService.getFieldsByTypes(["subform"])))
                      }
                      allowStyleTag
                    />
                  </Show>
                </div>
              </Show>
            </div>
          </Show>
        </div>
      </>
    );
  },
};

export default initIntegrationDefinition(EmailIntegration);
